import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { Dialog } from '../../components/Dialog.js';
import { listRecipes } from '../../api/recipes.js';
import type { Recipe } from '../../api/types.js';
import type { DietPlan, PlannerMealSlot } from '../../api/plannerTypes.js';

export interface RecipeSwapModalProps {
  open: boolean;
  onClose: () => void;
  slot: PlannerMealSlot;
  plan: DietPlan;
  dayTarget: { kcal: number; protein_g: number };
  onPick: (recipeId: number) => void;
}

function slotPct(plan: DietPlan, slot: PlannerMealSlot): number {
  switch (slot) {
    case 'breakfast':
      return plan.kcal_distribution_breakfast;
    case 'lunch':
      return plan.kcal_distribution_lunch;
    case 'dinner':
      return plan.kcal_distribution_dinner;
  }
}

function score(kTarget: number, kActual: number, pTarget: number, pActual: number): number {
  let p = 0;
  if (kTarget > 0) p += 0.7 * ((kTarget - kActual) / kTarget) ** 2;
  if (pTarget > 0) p += 0.3 * ((pTarget - pActual) / pTarget) ** 2;
  return p;
}

export function RecipeSwapModal({
  open,
  onClose,
  slot,
  plan,
  dayTarget,
  onPick,
}: RecipeSwapModalProps): ReactElement {
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    void (async () => {
      try {
        const list = await listRecipes({ meal_type: slot });
        if (!cancelled) setRecipes(list);
      } catch {
        if (!cancelled) setRecipes([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, slot]);

  const pct = slotPct(plan, slot);
  const kTarget = dayTarget.kcal * pct;
  const pTarget = dayTarget.protein_g * pct;
  const ranked = recipes
    .map((r) => ({
      id: r.id,
      title: r.title,
      kcal: r.per_portion.kcal,
      protein_g: r.per_portion.protein_g,
      penalty: score(kTarget, r.per_portion.kcal, pTarget, r.per_portion.protein_g),
    }))
    .sort((a, b) => a.penalty - b.penalty);

  return (
    <Dialog open={open} onClose={onClose} title={`Swap ${slot}`}>
      {loading ? (
        <div className="text-sm" style={{ color: 'var(--text-muted)' }}>Loading recipes…</div>
      ) : ranked.length === 0 ? (
        <div className="text-sm" style={{ color: 'var(--text-muted)' }}>
          No recipes of type “{slot}” available.
        </div>
      ) : (
        <ul className="flex flex-col gap-2" data-testid="swap-modal-list">
          {ranked.map((r) => (
            <li key={r.id}>
              <button
                type="button"
                data-testid={`swap-pick-${r.id}`}
                onClick={() => {
                  onPick(r.id);
                  onClose();
                }}
                className="flex w-full items-center justify-between rounded border px-3 py-2 text-left text-sm"
                style={{
                  background: 'var(--bg-surface)',
                  borderColor: 'var(--border)',
                  color: 'var(--text-main)',
                }}
              >
                <span>{r.title}</span>
                <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                  {Math.round(r.kcal)} kcal · P {r.protein_g.toFixed(1)}g · score {r.penalty.toFixed(2)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </Dialog>
  );
}